import type { RevenueTaxSourceDiscountEvidenceKind } from './generated/enums.js';

/**
 * Revenue Tax (PIS/COFINS on revenue) — shared codes and copy (ADR 0024, epic ALC-4408).
 *
 * Revenue Tax posts as a provision WITH the revenue: each posted revenue journal gets one linked
 * provision journal per book (Dr `DEDUCAO_*` / Cr the rail's "Apurado"), recorded row by row in
 * the `revenue_tax_provision` sub-ledger. The engine lives in `@erp/services/revenue-tax`; this
 * module holds what the API, the worker and the web share: slot codes, reason codes and pt-BR copy.
 */

/**
 * The taxes on the Revenue Tax pipeline, each identified by its liability slot code.
 * A string, not a two-value enum: CBS joins the same pipeline in 2027.
 */
export const REVENUE_TAX_SLOT_CODES = ['PIS_FATURAMENTO', 'COFINS_FATURAMENTO'] as const;

export type RevenueTaxSlotCode = (typeof REVENUE_TAX_SLOT_CODES)[number];

export function isRevenueTaxSlotCode(code: string | null | undefined): code is RevenueTaxSlotCode {
  return (REVENUE_TAX_SLOT_CODES as readonly string[]).includes(code ?? '');
}

/** Per tax: the slots its provision, cash-basis deferral and prepaid asset map to. */
export interface RevenueTaxSlotSet {
  /** The liability slot (accrual basis credits its rail's "Apurado"). */
  liabilitySlotCode: RevenueTaxSlotCode;
  /** The revenue deduction debited by the provision. */
  deductionSlotCode: string;
  /** Cash basis (Lucro Presumido): the provision is credited here until receipt. */
  deferredLiabilitySlotCode: string;
  /** Cash basis: tax paid on a customer advance before the revenue is recognized (asset). */
  prepaidAssetSlotCode: string;
}

export const REVENUE_TAX_SLOT_SETS: Readonly<Record<RevenueTaxSlotCode, RevenueTaxSlotSet>> =
  Object.freeze({
    PIS_FATURAMENTO: {
      liabilitySlotCode: 'PIS_FATURAMENTO',
      deductionSlotCode: 'DEDUCAO_PIS',
      deferredLiabilitySlotCode: 'PIS_FATURAMENTO_A_DIFERIR',
      prepaidAssetSlotCode: 'PIS_SOBRE_RECEITA_DIFERIDA',
    },
    COFINS_FATURAMENTO: {
      liabilitySlotCode: 'COFINS_FATURAMENTO',
      deductionSlotCode: 'DEDUCAO_COFINS',
      deferredLiabilitySlotCode: 'COFINS_FATURAMENTO_A_DIFERIR',
      prepaidAssetSlotCode: 'COFINS_SOBRE_RECEITA_DIFERIDA',
    },
  });

/**
 * The four cash-basis slots seeded for Revenue Tax (never required, never tax-slip payable — the
 * two prepaid-asset slots were flipped to `isTaxSlipPayable = false` by migration
 * 20261002030100_revenue_tax_constraints).
 */
export const REVENUE_TAX_DEFERRAL_SLOT_CODES = [
  'PIS_FATURAMENTO_A_DIFERIR',
  'COFINS_FATURAMENTO_A_DIFERIR',
  'PIS_SOBRE_RECEITA_DIFERIDA',
  'COFINS_SOBRE_RECEITA_DIFERIDA',
] as const;

/** Posting sources of the provision journal and of its mirror (registered in JournalService). */
export const REVENUE_TAX_PROVISION_POSTING_SOURCE = 'revenue_tax_provision';
export const REVENUE_TAX_PROVISION_REVERSAL_POSTING_SOURCE = 'revenue_tax_provision_reversal';
/** `documentType` of the provision journal; its `documentId` is the source revenue journal id. */
export const REVENUE_TAX_PROVISION_DOCUMENT_TYPE = 'REVENUE_TAX_PROVISION';
export const REVENUE_TAX_PROVISION_REVERSAL_DOCUMENT_TYPE = 'REVENUE_TAX_PROVISION_REVERSAL';
/**
 * Cash basis (ALC-4414): the release at receipt moves the tax from "a realizar no recebimento" to
 * "Apurado" (`documentId` = the receipt journal id); its mirror undoes it when the receipt is
 * reversed (`documentId` = the receipt reversal journal id).
 */
export const REVENUE_TAX_CASH_RELEASE_POSTING_SOURCE = 'revenue_tax_cash_release';
export const REVENUE_TAX_CASH_RELEASE_REVERSAL_POSTING_SOURCE = 'revenue_tax_cash_release_reversal';
export const REVENUE_TAX_CASH_RELEASE_DOCUMENT_TYPE = 'REVENUE_TAX_CASH_RELEASE';
export const REVENUE_TAX_CASH_RELEASE_REVERSAL_DOCUMENT_TYPE = 'REVENUE_TAX_CASH_RELEASE_REVERSAL';
/** `sourceTable` on every provision journal line; `sourceId` = the provision row id. */
export const REVENUE_TAX_PROVISION_SOURCE_TABLE = 'revenue_tax_provision';

/**
 * Stable reason codes stored on `revenue_tax_provision.reason_code` /
 * `revenue_tax_provision_movement.reason_code`. Never classify by message text.
 */
export const RevenueTaxReasonCode = {
  // NOT_APPLICABLE — nothing to post, by law or by arithmetic.
  ZERO_RATED: 'ZERO_RATED',
  EXEMPT: 'EXEMPT',
  OUTSIDE_BASE: 'OUTSIDE_BASE',
  NO_RULE_AT_DATE: 'NO_RULE_AT_DATE',
  ROUNDS_TO_ZERO: 'ROUNDS_TO_ZERO',
  NEGATIVE_BASE_LEFT_TO_ASSESSMENT: 'NEGATIVE_BASE_LEFT_TO_ASSESSMENT',
  /** EXPORT is a candidate class only; a source-bound reviewed decision is still missing. */
  EXPORT_ELIGIBILITY_EVIDENCE_REQUIRED: 'EXPORT_ELIGIBILITY_EVIDENCE_REQUIRED',
  /** A PENDING row whose account stopped being revenue (slot-mapped, retyped, made contra). */
  NOT_REVENUE_ACCOUNT: 'NOT_REVENUE_ACCOUNT',
  /**
   * Backfill heal (ALC-4415): a PENDING row whose assessment month is now before the entity's
   * Revenue Tax start month (the start month was moved later). Cancelled, never posted.
   */
  BEFORE_START_MONTH: 'BEFORE_START_MONTH',
  // PENDING — setup is missing; calling the engine again heals the row.
  MISSING_REVENUE_CLASS: 'MISSING_REVENUE_CLASS',
  MISSING_DEDUCTION_MAPPING: 'MISSING_DEDUCTION_MAPPING',
  MISSING_LIABILITY_MAPPING: 'MISSING_LIABILITY_MAPPING',
  MISSING_LIABILITY_RAIL: 'MISSING_LIABILITY_RAIL',
  LIABILITY_RAIL_UNUSABLE: 'LIABILITY_RAIL_UNUSABLE',
  MISSING_DEFERRED_LIABILITY_MAPPING: 'MISSING_DEFERRED_LIABILITY_MAPPING',
  /** A mapped source discount exists, but its revenue or dimensions are not uniquely attributable. */
  SOURCE_DISCOUNT_ALLOCATION_REQUIRED: 'SOURCE_DISCOUNT_ALLOCATION_REQUIRED',
  /** The mapped source contra exists, but no reviewed source-document decision binds it. */
  SOURCE_DISCOUNT_EVIDENCE_REQUIRED: 'SOURCE_DISCOUNT_EVIDENCE_REQUIRED',
  /** An older pending row froze a different base; correct/repost the source instead of rewriting it. */
  SOURCE_DISCOUNT_SOURCE_REPOST_REQUIRED: 'SOURCE_DISCOUNT_SOURCE_REPOST_REQUIRED',
  ACCOUNT_NOT_POSTABLE: 'ACCOUNT_NOT_POSTABLE',
  PERIOD_NOT_OPEN: 'PERIOD_NOT_OPEN',
  /**
   * Cash basis (decision I33): the receipt month is closed. The release waits there, never
   * re-dated into another month, and posts on the receipt date once the month is reopened.
   */
  PERIOD_CLOSED: 'PERIOD_CLOSED',
  /** A post-2026 receipt cannot release until the source is explicitly classified as legacy. */
  LEGACY_TRANSITION_CLASSIFICATION_REQUIRED: 'LEGACY_TRANSITION_CLASSIFICATION_REQUIRED',
  LEGACY_TRANSITION_SOURCE_DATE_MISSING: 'LEGACY_TRANSITION_SOURCE_DATE_MISSING',
  LEGACY_TRANSITION_SOURCE_DATE_INVALID: 'LEGACY_TRANSITION_SOURCE_DATE_INVALID',
  LEGACY_TRANSITION_SOURCE_AFTER_CUTOFF: 'LEGACY_TRANSITION_SOURCE_AFTER_CUTOFF',
  LEGACY_TRANSITION_SOURCE_EVIDENCE_MISSING: 'LEGACY_TRANSITION_SOURCE_EVIDENCE_MISSING',
  /**
   * Cash basis (ALC-4414 fix round 1, E3): an earlier release of the row was taken back by a
   * receipt reversal that is still owed, so "a realizar" does not hold this release yet. It posts
   * on its receipt date once the heal has posted that reversal.
   */
  AWAITING_RECEIPT_REVERSAL: 'AWAITING_RECEIPT_REVERSAL',
  COA_VERSION_NOT_POSTABLE: 'COA_VERSION_NOT_POSTABLE',
  // Reversal movements (`reason_code`).
  ORIGIN_ALREADY_CONSUMED: 'ORIGIN_ALREADY_CONSUMED',
  /**
   * The reversal is in a later month than the provision: the origin of the provision month stays
   * whole (the law lowers the base in the month of the cancel — SC COSIT 114/2014); the mirror
   * posts in the reversal month and the monthly assessment nets it.
   */
  ORIGIN_KEPT_OTHER_PERIOD: 'ORIGIN_KEPT_OTHER_PERIOD',
  /**
   * Decision I22 (ALC-4411): a reclassify reversed the revenue in a later month and posted it
   * again in the revenue month. The reposted origin of that month replaced the old one, so this
   * mirror lowers no month's tax (never in the coverage add-back of the reversal month).
   */
  ORIGIN_REPLACED_BY_REPOST: 'ORIGIN_REPLACED_BY_REPOST',
  REVERSED_BEFORE_POSTING: 'REVERSED_BEFORE_POSTING',
  // Reversal movements (`pending_reason`): the GL mirror is owed; the next engine call posts it.
  /** The provision row was still PENDING when the revenue was reversed. */
  PROVISION_NOT_POSTED: 'PROVISION_NOT_POSTED',
  /** Neither the stored accounts nor the slot mapping are postable in the reversal period. */
  REVERSAL_ACCOUNT_UNAVAILABLE: 'REVERSAL_ACCOUNT_UNAVAILABLE',
  // On the provision row while one of its reversal mirrors is owed.
  REVERSAL_MIRROR_PENDING: 'REVERSAL_MIRROR_PENDING',
  // Cash basis (ALC-4414), on `revenue_tax_provision_movement.reason_code`.
  /**
   * The invoice closed with part of it never received (a financial or unconditional discount
   * granted at receipt): the tax still "a realizar" is reversed against the deduction.
   */
  CASH_BASIS_DISCOUNT_NOT_RECEIVED: 'CASH_BASIS_DISCOUNT_NOT_RECEIVED',
  /** The receipt that closed the invoice with a discount was reversed: that reversal is undone. */
  CASH_BASIS_DISCOUNT_REOPENED: 'CASH_BASIS_DISCOUNT_REOPENED',
} as const;

export type RevenueTaxReasonCode = (typeof RevenueTaxReasonCode)[keyof typeof RevenueTaxReasonCode];

/** Reason codes that leave a row PENDING (retry heals it). */
export const REVENUE_TAX_PENDING_REASON_CODES: readonly RevenueTaxReasonCode[] = [
  RevenueTaxReasonCode.EXPORT_ELIGIBILITY_EVIDENCE_REQUIRED,
  RevenueTaxReasonCode.MISSING_REVENUE_CLASS,
  RevenueTaxReasonCode.MISSING_DEDUCTION_MAPPING,
  RevenueTaxReasonCode.MISSING_LIABILITY_MAPPING,
  RevenueTaxReasonCode.MISSING_LIABILITY_RAIL,
  RevenueTaxReasonCode.LIABILITY_RAIL_UNUSABLE,
  RevenueTaxReasonCode.MISSING_DEFERRED_LIABILITY_MAPPING,
  RevenueTaxReasonCode.SOURCE_DISCOUNT_ALLOCATION_REQUIRED,
  RevenueTaxReasonCode.SOURCE_DISCOUNT_EVIDENCE_REQUIRED,
  RevenueTaxReasonCode.SOURCE_DISCOUNT_SOURCE_REPOST_REQUIRED,
  RevenueTaxReasonCode.ACCOUNT_NOT_POSTABLE,
  RevenueTaxReasonCode.PERIOD_NOT_OPEN,
  RevenueTaxReasonCode.PERIOD_CLOSED,
  RevenueTaxReasonCode.LEGACY_TRANSITION_CLASSIFICATION_REQUIRED,
  RevenueTaxReasonCode.LEGACY_TRANSITION_SOURCE_DATE_MISSING,
  RevenueTaxReasonCode.LEGACY_TRANSITION_SOURCE_DATE_INVALID,
  RevenueTaxReasonCode.LEGACY_TRANSITION_SOURCE_AFTER_CUTOFF,
  RevenueTaxReasonCode.LEGACY_TRANSITION_SOURCE_EVIDENCE_MISSING,
  RevenueTaxReasonCode.AWAITING_RECEIPT_REVERSAL,
  RevenueTaxReasonCode.COA_VERSION_NOT_POSTABLE,
];

/** Stable error codes the engine THROWS (strict mode, programming errors). */
export const RevenueTaxErrorCode = {
  /** Reviewed export proof changed after the source was provisioned; source review is required. */
  EXPORT_ELIGIBILITY_EVIDENCE_REQUIRED: 'EXPORT_ELIGIBILITY_EVIDENCE_REQUIRED',
  /** Strict mode (`onMissing: 'throw'`): a row would have been PENDING; `reasonCode` says why. */
  REVENUE_TAX_PROVISION_PENDING: 'REVENUE_TAX_PROVISION_PENDING',
  REVENUE_TAX_SOURCE_JOURNAL_NOT_FOUND: 'REVENUE_TAX_SOURCE_JOURNAL_NOT_FOUND',
  REVENUE_TAX_REVERSAL_INVALID_SCOPE: 'REVENUE_TAX_REVERSAL_INVALID_SCOPE',
  REVENUE_TAX_REVERSAL_EXCEEDS_ORIGINAL: 'REVENUE_TAX_REVERSAL_EXCEEDS_ORIGINAL',
  REVENUE_TAX_REVERSAL_JOURNAL_MISMATCH: 'REVENUE_TAX_REVERSAL_JOURNAL_MISMATCH',
  /** The caller's reversal journal id does not exist (it must be posted before the call). */
  REVENUE_TAX_REVERSAL_JOURNAL_NOT_FOUND: 'REVENUE_TAX_REVERSAL_JOURNAL_NOT_FOUND',
  /** A Revenue Tax slot is missing from the global slot catalog (seed / migration gap). */
  REVENUE_TAX_SLOT_CATALOG_MISSING: 'REVENUE_TAX_SLOT_CATALOG_MISSING',
  /** The journal port returned no posted header in the requested book. */
  REVENUE_TAX_JOURNAL_NOT_POSTED: 'REVENUE_TAX_JOURNAL_NOT_POSTED',
  /**
   * Returned by the read-only reversal pre-check (never thrown by the engine): a provision origin
   * of the revenue journal is allocated to an approved guide. Informational only: owner decision
   * H1 (ALC-4411) lets a cancel proceed after the guide (the mirror posts with
   * `ORIGIN_ALREADY_CONSUMED`), so the AR paths never call the pre-check to refuse.
   */
  REVENUE_TAX_ORIGIN_ALREADY_ALLOCATED: 'REVENUE_TAX_ORIGIN_ALREADY_ALLOCATED',
  /** A tenant/entity override overlaps another override of the same key and scope. */
  REVENUE_TAX_RULE_OVERLAP: 'REVENUE_TAX_RULE_OVERLAP',
  REVENUE_TAX_RULE_INVALID: 'REVENUE_TAX_RULE_INVALID',
  /** Backfill (ALC-4415): another backfill run of the same entity is in progress. */
  REVENUE_TAX_BACKFILL_ALREADY_RUNNING: 'REVENUE_TAX_BACKFILL_ALREADY_RUNNING',
  /** Backfill: the entity has no Revenue Tax start month, or is not a Revenue Tax entity. */
  REVENUE_TAX_BACKFILL_NOT_STARTED: 'REVENUE_TAX_BACKFILL_NOT_STARTED',
  /** Backfill: the setup is not ready (rails missing, chart version blockers); `details` lists them. */
  REVENUE_TAX_BACKFILL_SETUP_INCOMPLETE: 'REVENUE_TAX_BACKFILL_SETUP_INCOMPLETE',
  /** Backfill: the cursor is not one this entity's preview / run chain returned (decision I28). */
  REVENUE_TAX_BACKFILL_INVALID_CURSOR: 'REVENUE_TAX_BACKFILL_INVALID_CURSOR',
  /**
   * Backfill: a guide is already approved for a month the run would provision, and the person did
   * not acknowledge it (`acknowledgeGuides`); `details.guides` lists them (decision I23).
   */
  REVENUE_TAX_BACKFILL_GUIDES_NEED_ACK: 'REVENUE_TAX_BACKFILL_GUIDES_NEED_ACK',
  /** Backfill: the entity's effective due basis is CASH; the S6 release owns it (decision I27). */
  REVENUE_TAX_BACKFILL_CASH_BASIS_UNSUPPORTED: 'REVENUE_TAX_BACKFILL_CASH_BASIS_UNSUPPORTED',
  /**
   * Backfill: the entity's effective due basis (accrual / cash) changed while the run was going.
   * Each posting transaction re-reads the config under the entity config lock and stops the run
   * here, so nothing posts under the old basis; what posted before the change stays posted.
   */
  REVENUE_TAX_BACKFILL_CONFIG_CHANGED: 'REVENUE_TAX_BACKFILL_CONFIG_CHANGED',
  /** CASH recovery: a confirmed source/evidence page changed before its atomic write. */
  REVENUE_TAX_BACKFILL_SOURCE_CHANGED: 'REVENUE_TAX_BACKFILL_SOURCE_CHANGED',
  REVENUE_TAX_CASH_BACKFILL_CONFIRMATION_REQUIRED:
    'REVENUE_TAX_CASH_BACKFILL_CONFIRMATION_REQUIRED',
  REVENUE_TAX_CASH_BACKFILL_SOURCE_UNSUPPORTED: 'REVENUE_TAX_CASH_BACKFILL_SOURCE_UNSUPPORTED',
} as const;

export type RevenueTaxErrorCode = (typeof RevenueTaxErrorCode)[keyof typeof RevenueTaxErrorCode];

export interface RevenueTaxCopy {
  ptBR: string;
  en: string;
}

/**
 * The label of the action that posts the provisions still pending (round 2 wires the button). It
 * lives here ONCE: the reasons below name it, and the web panel shows it.
 */
export const REVENUE_TAX_POST_PENDING_PROVISIONS_LABEL: Readonly<RevenueTaxCopy> = Object.freeze({
  ptBR: 'Lançar provisões pendentes',
  en: 'Post pending provisions',
});

/** The sentence every "fix this and the provision can post" reason ends with. */
export const REVENUE_TAX_AFTER_FIXING_COPY: Readonly<RevenueTaxCopy> = Object.freeze({
  ptBR: `Depois de corrigir, use "${REVENUE_TAX_POST_PENDING_PROVISIONS_LABEL.ptBR}" no painel da empresa.`,
  en: `After fixing it, use "${REVENUE_TAX_POST_PENDING_PROVISIONS_LABEL.en}" in the company panel.`,
});

const AFTER_FIXING_PT = REVENUE_TAX_AFTER_FIXING_COPY.ptBR;
const AFTER_FIXING_EN = REVENUE_TAX_AFTER_FIXING_COPY.en;

/** Actionable copy for every reason code (pt-BR is the product language). */
export const REVENUE_TAX_REASON_MESSAGES: Readonly<Record<RevenueTaxReasonCode, RevenueTaxCopy>> =
  Object.freeze({
    EXPORT_ELIGIBILITY_EVIDENCE_REQUIRED: {
      ptBR: 'A conta está marcada como exportação, mas a fonte ainda não tem uma decisão revisada vinculada ao lançamento, à conta e ao documento. Anexe a prova e revise a elegibilidade no detalhe da fatura.',
      en: 'The account is marked as export, but the source has no reviewed decision bound to the journal, account, and document. Attach the proof and review eligibility on the invoice detail.',
    },
    ZERO_RATED: {
      ptBR: 'Receita com alíquota zero de PIS/COFINS: nenhuma provisão é lançada.',
      en: 'Zero-rated revenue for PIS/COFINS: no provision is posted.',
    },
    EXEMPT: {
      ptBR: 'Receita isenta de PIS/COFINS: nenhuma provisão é lançada.',
      en: 'PIS/COFINS-exempt revenue: no provision is posted.',
    },
    OUTSIDE_BASE: {
      ptBR: 'Receita fora da base de cálculo de PIS/COFINS neste regime: nenhuma provisão é lançada.',
      en: 'Revenue outside the PIS/COFINS base under this regime: no provision is posted.',
    },
    NO_RULE_AT_DATE: {
      ptBR: 'Não há regra de PIS/COFINS vigente na data da receita (os padrões terminam em 31/12/2026). Cadastre uma regra própria se o tributo ainda se aplicar.',
      en: 'No PIS/COFINS rule is in force at the revenue date (defaults end on 2026-12-31). Add an override if the tax still applies.',
    },
    BEFORE_START_MONTH: {
      ptBR: 'A receita é de um mês anterior ao mês de início da provisão de PIS/COFINS da entidade: a provisão pendente foi cancelada e não será lançada.',
      en: "The revenue is from a month before the entity's PIS/COFINS provision start month: the pending provision was cancelled and will not be posted.",
    },
    NOT_REVENUE_ACCOUNT: {
      ptBR: 'A conta deixou de ser uma conta de receita para PIS/COFINS (passou a ser conta fiscal mapeada, redutora ou de outro tipo): nenhuma provisão é lançada.',
      en: 'The account is no longer a PIS/COFINS revenue account (it became a mapped tax account, a contra account or another type): no provision is posted.',
    },
    ROUNDS_TO_ZERO: {
      ptBR: 'O tributo calculado arredonda para R$ 0,00: nenhuma provisão é lançada.',
      en: 'The computed tax rounds to zero: no provision is posted.',
    },
    NEGATIVE_BASE_LEFT_TO_ASSESSMENT: {
      ptBR: 'A base líquida da receita é negativa (por exemplo, um desconto posterior). Nada é lançado agora; a apuração mensal considera o total do mês.',
      en: 'The net revenue base is negative (for example, a later discount). Nothing is posted now; the monthly assessment uses the month total.',
    },
    MISSING_REVENUE_CLASS: {
      ptBR: `A conta de receita não tem Classe de Receita. Defina a classe no plano de contas. ${AFTER_FIXING_PT}`,
      en: `The revenue account has no Revenue Class. Set it in the chart of accounts. ${AFTER_FIXING_EN}`,
    },
    MISSING_DEDUCTION_MAPPING: {
      ptBR: `A conta de dedução da receita (DEDUCAO_PIS / DEDUCAO_COFINS) não está mapeada. Mapeie-a nas configurações fiscais. ${AFTER_FIXING_PT}`,
      en: `The revenue deduction account (DEDUCAO_PIS / DEDUCAO_COFINS) is not mapped. Map it in the tax settings. ${AFTER_FIXING_EN}`,
    },
    MISSING_LIABILITY_MAPPING: {
      ptBR: `A conta de PIS/COFINS a recolher não está mapeada. Mapeie-a nas configurações fiscais. ${AFTER_FIXING_PT}`,
      en: `The PIS/COFINS payable account is not mapped. Map it in the tax settings. ${AFTER_FIXING_EN}`,
    },
    MISSING_LIABILITY_RAIL: {
      ptBR: `A estrutura Apurado / Em Guias de PIS/COFINS ainda não foi criada para este plano de contas. Conclua a configuração do passivo fiscal. ${AFTER_FIXING_PT}`,
      en: `The PIS/COFINS Apurado / guides structure does not exist for this chart yet. Finish the tax liability setup. ${AFTER_FIXING_EN}`,
    },
    LIABILITY_RAIL_UNUSABLE: {
      ptBR: `A estrutura Apurado / Em Guias de PIS/COFINS está inconsistente com o mapeamento fiscal. Revise o mapeamento. ${AFTER_FIXING_PT}`,
      en: `The PIS/COFINS Apurado / guides structure does not match the tax mapping. Review the mapping. ${AFTER_FIXING_EN}`,
    },
    MISSING_DEFERRED_LIABILITY_MAPPING: {
      ptBR: `Entidade no regime de caixa: mapeie a conta de PIS/COFINS a realizar no recebimento. ${AFTER_FIXING_PT}`,
      en: `Cash-basis entity: map the PIS/COFINS "to be realized at receipt" account. ${AFTER_FIXING_EN}`,
    },
    SOURCE_DISCOUNT_ALLOCATION_REQUIRED: {
      ptBR: 'O lançamento de origem contém desconto incondicional na conta mapeada, mas não permite atribuí-lo com segurança a uma única receita, classificação e dimensão. Corrija e reposte o documento ou lançamento de origem com essa atribuição explícita; nenhuma provisão foi lançada.',
      en: 'The source journal contains an unconditional discount in the mapped account, but it cannot be safely attributed to one revenue account, classification, and dimension. Correct and repost the source document or journal with an explicit attribution; no provision was posted.',
    },
    SOURCE_DISCOUNT_EVIDENCE_REQUIRED: {
      en: 'Review the original document that states this unconditional discount before posting Revenue Tax.',
      ptBR: 'Revise o documento de origem que declara este desconto incondicional antes de contabilizar o PIS/COFINS.',
    },
    SOURCE_DISCOUNT_SOURCE_REPOST_REQUIRED: {
      ptBR: 'Esta provisão pendente congelou uma base anterior à dedução documentada na origem. Para preservar o histórico, corrija, estorne e reposte o documento ou lançamento de origem; a base pendente não será reescrita.',
      en: 'This pending provision froze a base from before the documented source deduction. To preserve history, correct, reverse, and repost the source document or journal; the pending base will not be rewritten.',
    },
    ACCOUNT_NOT_POSTABLE: {
      ptBR: `A conta mapeada para a provisão está inativa, não aceita lançamentos ou não pertence ao plano de contas do período. Corrija o mapeamento. ${AFTER_FIXING_PT}`,
      en: `The mapped provision account is inactive, not postable or outside the period chart. Fix the mapping. ${AFTER_FIXING_EN}`,
    },
    PERIOD_NOT_OPEN: {
      ptBR: `O período contábil da receita não está aberto. Reabra o período. ${AFTER_FIXING_PT}`,
      en: `The accounting period of the revenue is not open. Reopen it. ${AFTER_FIXING_EN}`,
    },
    PERIOD_CLOSED: {
      ptBR: 'Regime de caixa: o PIS/COFINS deste recebimento aguarda mês fechado. Reabra o mês do recebimento para lançar a liberação na data do recebimento.',
      en: 'Cash basis: the PIS/COFINS of this receipt waits on a closed month. Reopen the receipt month to post the release on the receipt date.',
    },
    LEGACY_TRANSITION_CLASSIFICATION_REQUIRED: {
      ptBR: 'Recebimento após 31/12/2026: classifique a operação como legado de PIS/COFINS e informe a evidência antes de liberar o tributo diferido.',
      en: 'Receipt after December 31, 2026: classify the operation as PIS/COFINS legacy and provide the evidence before releasing deferred tax.',
    },
    LEGACY_TRANSITION_SOURCE_DATE_MISSING: {
      ptBR: 'A data da operação original não foi encontrada. Revise a competência/data da fatura, nota de débito ou reconhecimento contratual antes de liberar o tributo diferido.',
      en: 'The original operation date was not found. Review the invoice, debit-note, or contract-recognition date before releasing deferred tax.',
    },
    LEGACY_TRANSITION_SOURCE_DATE_INVALID: {
      ptBR: 'A data informada na origem da fatura, nota de débito ou reconhecimento contratual não é válida. Corrija a origem antes de liberar o tributo diferido.',
      en: 'The date recorded on the invoice, debit note, or contract-recognition source is invalid. Correct the source before releasing deferred tax.',
    },
    LEGACY_TRANSITION_SOURCE_AFTER_CUTOFF: {
      ptBR: 'A operação original é posterior a 31/12/2026 e não pode usar o tratamento ordinário de legado de PIS/COFINS. Revise o tratamento da transição.',
      en: 'The original operation is after December 31, 2026 and cannot use ordinary PIS/COFINS legacy treatment. Review the transition treatment.',
    },
    LEGACY_TRANSITION_SOURCE_EVIDENCE_MISSING: {
      ptBR: 'A origem canônica da operação não foi encontrada ou não é suportada para a transição. Revise o vínculo da fatura, nota de débito ou reconhecimento contratual.',
      en: 'The canonical operation source was not found or is unsupported for the transition. Review the invoice, debit-note, or contract-recognition link.',
    },
    AWAITING_RECEIPT_REVERSAL: {
      ptBR: 'Regime de caixa: o PIS/COFINS deste recebimento aguarda o lançamento do estorno de um recebimento anterior da mesma fatura. Corrija o motivo indicado nesse estorno; a liberação é lançada na data do recebimento.',
      en: 'Cash basis: the PIS/COFINS of this receipt waits for the reversal of an earlier receipt of the same invoice to post. Fix the reason shown on that reversal; the release posts on the receipt date.',
    },
    COA_VERSION_NOT_POSTABLE: {
      ptBR: `O plano de contas do período não aceita lançamentos. Publique a versão do plano de contas. ${AFTER_FIXING_PT}`,
      en: `The period chart of accounts is not postable. Publish the chart version. ${AFTER_FIXING_EN}`,
    },
    ORIGIN_ALREADY_CONSUMED: {
      ptBR: 'A provisão já foi consumida por uma guia de PIS/COFINS. O estorno foi lançado na contabilidade; a diferença será acertada na apuração mensal.',
      en: 'The provision was already consumed by a PIS/COFINS guide. The reversal was posted to the ledger; the difference is trued up in the monthly assessment.',
    },
    REVERSED_BEFORE_POSTING: {
      ptBR: 'A receita foi estornada antes de a provisão ser lançada: nada a estornar.',
      en: 'The revenue was reversed before the provision was posted: nothing to reverse.',
    },
    ORIGIN_KEPT_OTHER_PERIOD: {
      ptBR: 'O estorno é de um mês posterior ao da receita: a provisão do mês original continua inteira para a guia daquele mês, e o estorno reduz a apuração do mês do cancelamento.',
      en: "The reversal is in a later month than the revenue: the original month keeps its full provision for that month's guide, and the reversal lowers the assessment of the cancellation month.",
    },
    ORIGIN_REPLACED_BY_REPOST: {
      ptBR: 'A fatura foi reclassificada: a receita foi estornada e lançada de novo no mesmo mês de apuração. A nova provisão substitui a anterior (inclusive o que a guia já pagou), e o estorno não reduz a apuração do mês da reclassificação.',
      en: 'The invoice was reclassified: the revenue was reversed and posted again in the same assessment month. The new provision replaces the old one (including what the guide already paid), and the reversal does not lower the assessment of the reclassification month.',
    },
    PROVISION_NOT_POSTED: {
      ptBR: 'A receita foi estornada enquanto a provisão ainda estava pendente. Quando a configuração for concluída e a provisão for lançada, o estorno dela é lançado na data do estorno da receita.',
      en: 'The revenue was reversed while its provision was still pending. Once the setup is finished and the provision posts, its reversal posts on the revenue reversal date.',
    },
    REVERSAL_ACCOUNT_UNAVAILABLE: {
      ptBR: `O estorno da provisão de PIS/COFINS ainda não foi lançado: as contas de dedução ou de passivo não aceitam lançamentos no período do estorno. Reative a conta ou corrija o mapeamento fiscal. ${AFTER_FIXING_PT}`,
      en: `The PIS/COFINS provision reversal is not posted yet: the deduction or liability account is not postable in the reversal period. Reactivate the account or fix the tax mapping. ${AFTER_FIXING_EN}`,
    },
    CASH_BASIS_DISCOUNT_NOT_RECEIVED: {
      ptBR: 'Regime de caixa: parte da fatura não foi recebida (desconto concedido no recebimento). O PIS/COFINS a realizar dessa parte foi estornado contra a dedução da receita.',
      en: 'Cash basis: part of the invoice was never received (a discount granted at receipt). The PIS/COFINS still to be realized on that part was reversed against the revenue deduction.',
    },
    CASH_BASIS_DISCOUNT_REOPENED: {
      ptBR: 'Regime de caixa: o recebimento que quitou a fatura com desconto foi estornado. O estorno do PIS/COFINS a realizar sobre o desconto foi desfeito.',
      en: 'Cash basis: the receipt that closed the invoice with a discount was reversed. The reversal of the PIS/COFINS still to be realized on the discount was undone.',
    },
    REVERSAL_MIRROR_PENDING: {
      ptBR: `Há um estorno desta provisão ainda não lançado na contabilidade. Corrija o motivo indicado no estorno. ${AFTER_FIXING_PT}`,
      en: `A reversal of this provision is not posted to the ledger yet. Fix the reason shown on the reversal. ${AFTER_FIXING_EN}`,
    },
  });

/**
 * Decision I33: the heal report line of a cash-basis release that waits on a closed month
 * (`PERIOD_CLOSED`), for the month `YYYY-MM`: "aguarda mês fechado 10/2026".
 */
export function revenueTaxPeriodClosedCopy(periodMonth: string): RevenueTaxCopy {
  const [year, month] = periodMonth.split('-');
  return {
    ptBR: `aguarda mês fechado ${month}/${year}`,
    en: `waiting on closed month ${month}/${year}`,
  };
}

/** Actionable copy for every thrown error code. */
export const REVENUE_TAX_ERROR_MESSAGES: Readonly<Record<RevenueTaxErrorCode, RevenueTaxCopy>> =
  Object.freeze({
    EXPORT_ELIGIBILITY_EVIDENCE_REQUIRED:
      REVENUE_TAX_REASON_MESSAGES.EXPORT_ELIGIBILITY_EVIDENCE_REQUIRED,
    REVENUE_TAX_PROVISION_PENDING: {
      ptBR: 'A provisão de PIS/COFINS não pode ser lançada: a configuração fiscal está incompleta.',
      en: 'The PIS/COFINS provision cannot be posted: the tax setup is incomplete.',
    },
    REVENUE_TAX_SOURCE_JOURNAL_NOT_FOUND: {
      ptBR: 'O lançamento de receita informado não foi encontrado.',
      en: 'The revenue journal was not found.',
    },
    REVENUE_TAX_REVERSAL_INVALID_SCOPE: {
      ptBR: 'O estorno parcial informado é inválido: a fração deve estar entre 0 e 1 e os valores devem ser positivos.',
      en: 'The partial reversal is invalid: the share must be between 0 and 1 and amounts must be positive.',
    },
    REVENUE_TAX_REVERSAL_EXCEEDS_ORIGINAL: {
      ptBR: 'O estorno excede a receita original ainda não estornada.',
      en: 'The reversal exceeds the original revenue not yet reversed.',
    },
    REVENUE_TAX_REVERSAL_JOURNAL_MISMATCH: {
      ptBR: 'O estorno não corresponde à receita original: o lançamento de estorno deve ser do mesmo livro e entidade, em um período aberto que contenha a data do estorno.',
      en: 'The reversal does not match the original revenue: the reversal journal must be in the same book and entity, in an open period that contains the reversal date.',
    },
    REVENUE_TAX_REVERSAL_JOURNAL_NOT_FOUND: {
      ptBR: 'O lançamento de estorno informado não foi encontrado. Lance o estorno da receita antes de estornar a provisão de PIS/COFINS.',
      en: 'The reversal journal was not found. Post the revenue reversal before reversing the PIS/COFINS provision.',
    },
    REVENUE_TAX_SLOT_CATALOG_MISSING: {
      ptBR: 'O catálogo de contas fiscais não tem as contas de PIS/COFINS sobre faturamento. Execute a atualização do catálogo do sistema e tente de novo.',
      en: 'The tax slot catalog has no PIS/COFINS on revenue slots. Run the system catalog update and try again.',
    },
    REVENUE_TAX_JOURNAL_NOT_POSTED: {
      ptBR: 'O lançamento de PIS/COFINS não foi gerado no livro contábil esperado. Tente de novo; se persistir, contate o suporte.',
      en: 'The PIS/COFINS journal was not posted in the expected book. Try again; if it persists, contact support.',
    },
    REVENUE_TAX_ORIGIN_ALREADY_ALLOCATED: {
      ptBR: 'Não é possível estornar a receita: a provisão de PIS/COFINS dela já está em uma guia aprovada. Estorne a aprovação da guia primeiro.',
      en: 'The revenue cannot be reversed: its PIS/COFINS provision is already in an approved guide. Reverse the guide approval first.',
    },
    REVENUE_TAX_RULE_OVERLAP: {
      ptBR: 'Já existe uma regra de PIS/COFINS para esta combinação com vigência sobreposta. Ajuste as datas de vigência.',
      en: 'A PIS/COFINS rule for this combination already covers part of this period. Adjust the validity dates.',
    },
    REVENUE_TAX_RULE_INVALID: {
      ptBR: 'Regra de PIS/COFINS inválida: a alíquota é obrigatória para receita tributada e deve ficar vazia nos demais casos.',
      en: 'Invalid PIS/COFINS rule: the rate is required for taxed revenue and must be empty otherwise.',
    },
    REVENUE_TAX_BACKFILL_ALREADY_RUNNING: {
      ptBR: 'Já existe uma execução em andamento para esta entidade. Aguarde a conclusão e tente de novo.',
      en: 'A run is already in progress for this entity. Wait for it to finish and try again.',
    },
    REVENUE_TAX_BACKFILL_NOT_STARTED: {
      ptBR: 'A entidade não tem mês de início da provisão de PIS/COFINS. Defina o mês de início na configuração fiscal antes da execução.',
      en: 'The entity has no PIS/COFINS provision start month. Set the start month in the tax setup before the run.',
    },
    REVENUE_TAX_BACKFILL_SETUP_INCOMPLETE: {
      ptBR: 'A configuração de PIS/COFINS da entidade está incompleta para os meses da execução. Corrija os itens indicados e tente de novo.',
      en: "The entity's PIS/COFINS setup is incomplete for the months of the run. Fix the listed items and try again.",
    },
    REVENUE_TAX_BACKFILL_INVALID_CURSOR: {
      ptBR: 'O cursor informado não é válido para a execução desta entidade. Recomece sem cursor.',
      en: "The cursor is not valid for this entity's run. Start again without a cursor.",
    },
    REVENUE_TAX_BACKFILL_GUIDES_NEED_ACK: {
      ptBR: 'Já existem guias de PIS/COFINS aprovadas para meses da execução. Revise a lista de guias e confirme para continuar.',
      en: 'PIS/COFINS guides are already approved for months of the run. Review the guide list and confirm to continue.',
    },
    REVENUE_TAX_BACKFILL_CASH_BASIS_UNSUPPORTED: {
      ptBR: 'No regime de caixa, esta execução trata recebimentos e recupera somente as fontes documentais indicadas na conferência. Receitas manuais sem vínculo de recebimento não são incluídas.',
      en: 'For cash-basis entities, this run handles receipts and recovers only the document sources shown during review. Manual revenue without a receipt link is not included.',
    },
    REVENUE_TAX_BACKFILL_CONFIG_CHANGED: {
      ptBR: `O reconhecimento das receitas (competência ou caixa) mudou durante a execução, e ela parou. O que foi lançado antes da mudança continua lançado. Clique em "${REVENUE_TAX_POST_PENDING_PROVISIONS_LABEL.ptBR}" de novo para continuar com a opção nova.`,
      en: `Revenue recognition (accrual or cash) changed during the run, so the run stopped. What was posted before the change stays posted. Click "${REVENUE_TAX_POST_PENDING_PROVISIONS_LABEL.en}" again to continue with the new option.`,
    },
    REVENUE_TAX_BACKFILL_SOURCE_CHANGED: {
      ptBR: 'Uma fonte de receita ou seu recebimento mudou desde a conferência. Confira novamente antes de lançar.',
      en: 'A revenue source or its receipt changed since review. Review it again before posting.',
    },
    REVENUE_TAX_CASH_BACKFILL_CONFIRMATION_REQUIRED: {
      ptBR: 'Confirme as fontes de receita na conferência antes de recuperar suas provisões.',
      en: 'Confirm the revenue sources in the review before recovering their provisions.',
    },
    REVENUE_TAX_CASH_BACKFILL_SOURCE_UNSUPPORTED: {
      ptBR: 'Uma fonte confirmada não admite recuperação de provisões pelo regime de caixa. Revise a origem antes de continuar.',
      en: 'A confirmed source does not support cash-basis provision recovery. Review its origin before continuing.',
    },
  });

/**
 * Codes the backfill REPORTS per item (never thrown): the run goes on and lists what it could not
 * post, with the month the operator must reopen (ALC-4415).
 */
export const RevenueTaxBackfillCode = {
  /** A revenue journal (or the later reversal it pairs with) is in a closed period. */
  REVENUE_TAX_BACKFILL_PERIOD_CLOSED: 'REVENUE_TAX_BACKFILL_PERIOD_CLOSED',
  /** A pending provision row whose own period is closed: listed, not posted. */
  REVENUE_TAX_BACKFILL_PENDING_PERIOD_CLOSED: 'REVENUE_TAX_BACKFILL_PENDING_PERIOD_CLOSED',
  /** An owed reversal mirror whose period is closed: listed, stays owed. */
  REVENUE_TAX_BACKFILL_MIRROR_PERIOD_CLOSED: 'REVENUE_TAX_BACKFILL_MIRROR_PERIOD_CLOSED',
  /** The engine refused one journal; the run went on with the others. */
  REVENUE_TAX_BACKFILL_JOURNAL_FAILED: 'REVENUE_TAX_BACKFILL_JOURNAL_FAILED',
  /**
   * A guide of the month was approved and posted as expense (treatment CLASSIFY): the month is
   * skipped, or its tax would be deducted twice (decision I23).
   */
  REVENUE_TAX_BACKFILL_GUIDE_ALREADY_BOOKED: 'REVENUE_TAX_BACKFILL_GUIDE_ALREADY_BOOKED',
  /**
   * The journal credits revenue AND already books PIS/COFINS (a line on a deduction account, or a
   * credit on a PIS/COFINS liability account): skipped, never provisioned again (decision I26).
   */
  REVENUE_TAX_BACKFILL_LOOKS_PRE_TAXED: 'REVENUE_TAX_BACKFILL_LOOKS_PRE_TAXED',
  /**
   * The start month moved (either way) between the page read and the heal's config lock: the
   * journal is left as it is and reported; the next run decides it with the new start month.
   */
  REVENUE_TAX_BACKFILL_SKIPPED_CONFIG_CHANGED: 'REVENUE_TAX_BACKFILL_SKIPPED_CONFIG_CHANGED',
  /**
   * The cash-basis release step (decision I36) threw: the provisions of the run stay posted, the
   * releases stay owed and the next run tries them again (ALC-4414).
   */
  REVENUE_TAX_BACKFILL_RELEASE_STEP_FAILED: 'REVENUE_TAX_BACKFILL_RELEASE_STEP_FAILED',
} as const;

export type RevenueTaxBackfillCode =
  (typeof RevenueTaxBackfillCode)[keyof typeof RevenueTaxBackfillCode];

/**
 * Copy for a backfill item. `month` is `YYYY-MM` and always reads `MM/YYYY` (the one month format
 * of the screen and the MCP); `taxes` are the tax names of the guides (`['PIS', 'COFINS']`) for
 * `REVENUE_TAX_BACKFILL_GUIDE_ALREADY_BOOKED`. `count` is the entries concerned; no sentence
 * repeats it since round 2b (the screen shows the count on its own), the field stays so callers
 * keep compiling.
 *
 * The run is "a execução" / "the run" and its button is `REVENUE_TAX_POST_PENDING_PROVISIONS_LABEL`;
 * a journal entry is "um lançamento" / "an entry". Never "reprocessamento".
 */
export function revenueTaxBackfillMessage(
  code: RevenueTaxBackfillCode,
  params: { month: string; count?: number; taxes?: readonly string[] },
): RevenueTaxCopy {
  const [yearText, monthText] = params.month.split('-');
  const mmYyyy = `${monthText}/${yearText}`;
  const buttonPt = REVENUE_TAX_POST_PENDING_PROVISIONS_LABEL.ptBR;
  const buttonEn = REVENUE_TAX_POST_PENDING_PROVISIONS_LABEL.en;
  switch (code) {
    case 'REVENUE_TAX_BACKFILL_PERIOD_CLOSED':
      return {
        ptBR: `${mmYyyy} está fechado: reabra o período para lançar; a provisão entra com a data da própria receita.`,
        en: `${mmYyyy} is closed: reopen the period to post; the provision is posted with the revenue's own date.`,
      };
    case 'REVENUE_TAX_BACKFILL_PENDING_PERIOD_CLOSED':
      return {
        ptBR: `A provisão pendente é do período ${mmYyyy}, que está fechado: reabra o período ${mmYyyy} para lançá-la.`,
        en: `The pending provision belongs to ${mmYyyy}, which is closed: reopen ${mmYyyy} to post it.`,
      };
    case 'REVENUE_TAX_BACKFILL_MIRROR_PERIOD_CLOSED':
      return {
        ptBR: `O estorno da provisão de ${mmYyyy} não foi lançado porque o período está fechado. Reabra o período para lançá-lo.`,
        en: `The provision reversal of ${mmYyyy} was not posted because the period is closed. Reopen the period to post it.`,
      };
    case 'REVENUE_TAX_BACKFILL_JOURNAL_FAILED':
      return {
        ptBR: `Não foi possível lançar a provisão de um lançamento de ${mmYyyy}; os demais foram processados. Tente de novo; se persistir, contate o suporte.`,
        en: `The provision of one ${mmYyyy} entry could not be posted; the others were processed. Try again; if it persists, contact support.`,
      };
    case 'REVENUE_TAX_BACKFILL_GUIDE_ALREADY_BOOKED': {
      const taxes = params.taxes && params.taxes.length > 0 ? params.taxes : ['PIS/COFINS'];
      const plural = taxes.length > 1;
      return {
        ptBR: `${plural ? 'As guias' : 'A guia'} de ${taxes.join(' e ')} de ${mmYyyy} já ${plural ? 'foram lançadas' : 'foi lançada'} como despesa. Estorne a aprovação da guia, clique em "${buttonPt}" e aprove a guia de novo.`,
        en: `The ${taxes.join(' and ')} ${plural ? 'guides' : 'guide'} of ${mmYyyy} ${plural ? 'were' : 'was'} already booked as expense. Reverse the guide approval, click "${buttonEn}" and approve the guide again.`,
      };
    }
    case 'REVENUE_TAX_BACKFILL_LOOKS_PRE_TAXED':
      return {
        ptBR: `Lançamento de receita de ${mmYyyy} que já tem PIS/COFINS no próprio lançamento: não recebe provisão automática.`,
        en: `A ${mmYyyy} revenue entry that already books PIS/COFINS in the entry itself: it gets no automatic provision.`,
      };
    case 'REVENUE_TAX_BACKFILL_SKIPPED_CONFIG_CHANGED':
      return {
        ptBR: `O mês de início da provisão mudou durante a execução: o lançamento de receita de ${mmYyyy} não foi alterado. Clique em "${buttonPt}" de novo para tratá-lo.`,
        en: `The provision start month changed during the run: the ${mmYyyy} revenue entry was left as it was. Click "${buttonEn}" again to handle it.`,
      };
    case 'REVENUE_TAX_BACKFILL_RELEASE_STEP_FAILED':
      return {
        ptBR: 'Não foi possível lançar as liberações de PIS/COFINS do regime de caixa; as provisões desta execução foram mantidas. Tente de novo; se persistir, contate o suporte.',
        en: 'The cash-basis PIS/COFINS releases could not be posted; the provisions of this run were kept. Try again; if it persists, contact support.',
      };
  }
}

/** What the run does with a guide already approved for a backfilled month (decision I23). */
export type RevenueTaxBackfillGuideAction = 'BLOCKED' | 'WILL_MATCH' | 'WILL_NOT_MATCH';

/** Copy for one guide of `guidesAlreadyApproved`. `month` is `YYYY-MM`; `tax` is `PIS` / `COFINS`. */
export function revenueTaxBackfillGuideMessage(
  action: RevenueTaxBackfillGuideAction,
  params: { month: string; tax: string },
): RevenueTaxCopy {
  const [yearText, monthText] = params.month.split('-');
  const mmYyyy = `${monthText}/${yearText}`;
  switch (action) {
    case 'BLOCKED':
      return revenueTaxBackfillMessage('REVENUE_TAX_BACKFILL_GUIDE_ALREADY_BOOKED', {
        month: params.month,
        taxes: [params.tax],
      });
    case 'WILL_MATCH':
      return {
        ptBR: `1 guia de ${params.tax} de ${mmYyyy} já foi aprovada. A provisão nova será ligada a essa guia, e o imposto não será cobrado duas vezes.`,
        en: `1 ${params.tax} guide of ${mmYyyy} was already approved. The new provision will be linked to that guide, and the tax will not be charged twice.`,
      };
    case 'WILL_NOT_MATCH':
      return {
        ptBR: `1 guia de ${params.tax} de ${mmYyyy} já foi aprovada, mas cobre só parte do imposto. A diferença vai precisar de uma guia complementar.`,
        en: `1 ${params.tax} guide of ${mmYyyy} was already approved, but covers only part of the tax. The difference will need a supplementary guide.`,
      };
  }
}

/** Catalog identity for the existing Brazilian PIS/COFINS own-tax liability slots. */
export const PIS_COFINS_CATALOG_SLOT_CODES: Readonly<Record<string, string>> = {
  '8109': 'PIS_FATURAMENTO',
  '6912': 'PIS_FATURAMENTO',
  '2172': 'COFINS_FATURAMENTO',
  '5856': 'COFINS_FATURAMENTO',
};

export function resolvePisCofinsCatalogCode(
  code: string | null | undefined,
  slotCode: string | null | undefined,
): string | null {
  return code && slotCode && PIS_COFINS_CATALOG_SLOT_CODES[code] === slotCode ? code : null;
}

export type RevenueTaxSourceDiscountSourceKind = 'MANUAL_ENTRY';

export interface RevenueTaxSourceDiscountEvidenceOption {
  evidenceKind: RevenueTaxSourceDiscountEvidenceKind;
  evidenceId: string;
  label: string;
  documentHashSha256: string;
  resourceVersion: string;
}

export interface RevenueTaxSourceDiscountPreviewResponse {
  state: 'NOT_CANDIDATE' | 'NEEDS_EVIDENCE' | 'APPROVED' | 'REFUSED';
  sourceKind: RevenueTaxSourceDiscountSourceKind;
  sourceId: string;
  sourceJournalId: string | null;
  entityId: string | null;
  bookId: string | null;
  journalDate: string | null;
  currency: string | null;
  revenueAccountId: string | null;
  discountAccountId: string | null;
  grossRevenueAmount: string | null;
  discountAmount: string | null;
  netRevenueAmount: string | null;
  resourceVersion: string | null;
  refusalReason: string | null;
  evidenceOptions: RevenueTaxSourceDiscountEvidenceOption[];
  decisionId: string | null;
}

export interface CreateRevenueTaxSourceDiscountDecisionRequest {
  sourceKind: RevenueTaxSourceDiscountSourceKind;
  sourceId: string;
  resourceVersion: string;
  evidenceKind: RevenueTaxSourceDiscountEvidenceKind;
  evidenceId: string;
  evidenceCode: string;
  evidenceDescription: string;
  statedOnSourceDocument: true;
  independentOfFutureEvent: true;
}

export interface RevenueTaxSourceDiscountDecisionResponse {
  decisionId: string;
  sourceJournalId: string;
  revenueAccountId: string;
  discountAccountId: string;
  grossRevenueAmount: string;
  discountAmount: string;
  netRevenueAmount: string;
  evidenceKind: RevenueTaxSourceDiscountEvidenceKind;
  evidenceId: string;
  evidenceDocumentHashSha256: string;
  calculationFingerprint: string;
  reviewedByUserId: string;
  reviewedAt: string;
}
